/**
 * Custom error class for Base64 operations
 */
class Base64Error extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'Base64Error';
    }
}

/**
 * Interface for Base64 operation options
 */
interface IBase64Options {
    padding?: boolean;
    urlSafe?: boolean;
}

/**
 * Default encryption key for Base64 operations
 */
const DEFAULT_KEY = 'GPEMS_3212223';

/**
 * Universal btoa that works in both browser and Node.js
 */
function universalBtoa(str: string): string {
    if (typeof btoa !== 'undefined') {
        return btoa(str);
    }
    // Node.js fallback (Node 18+ has btoa, but this is for safety)
    return Buffer.from(str, 'binary').toString('base64');
}

/**
 * Universal atob that works in both browser and Node.js
 */
function universalAtob(str: string): string {
    if (typeof atob !== 'undefined') {
        return atob(str);
    }
    // Node.js fallback (Node 18+ has atob, but this is for safety)
    return Buffer.from(str, 'base64').toString('binary');
}

/**
 * Utility class for handling Base64 encoding/decoding operations with additional security features
 */
export class Base64Utils {
    private static readonly URL_SAFE_CHARS: { [key: string]: string } = {
        '+': '-',
        '/': '_',
        '=': ''
    };

    private static readonly STANDARD_CHARS: { [key: string]: string } = {
        '-': '+',
        '_': '/',
    };

    /**
     * Encodes data with an optional key and customization options
     * @param data - String to encode
     * @param key - Optional encryption key
     * @param options - Encoding options
     * @returns Encoded string
     * @throws Base64Error
     */
    public static encode(
        data: string,
        key?: string,
        options: IBase64Options = { padding: true, urlSafe: true }
    ): string {
        try {
            if (!data) {
                throw new Base64Error('Data to encode cannot be empty');
            }

            let encoded = universalBtoa(data);

            if (key) {
                const keyEncoded = universalBtoa(key);
                const mid = Math.floor(encoded.length / 2);
                encoded = encoded.slice(0, mid) + keyEncoded + encoded.slice(mid);
            }

            if (options.urlSafe) {
                encoded = Base64Utils.makeUrlSafe(encoded);
            }

            if (!options.padding) {
                encoded = encoded.replace(/=+$/, '');
            }

            return encoded;
        } catch (error) {
            if (error instanceof Base64Error) {
                throw error;
            }
            throw new Base64Error(`Encoding failed: ${(error as Error).message}`);
        }
    }

    /**
     * Decodes Base64 string with an optional key
     * @param data - String to decode
     * @param key - Optional decryption key
     * @param options - Decoding options
     * @returns Decoded string
     * @throws Base64Error
     */
    public static decode(
        data: string,
        key?: string,
        options: IBase64Options = { padding: true, urlSafe: true }
    ): string {
        try {
            if (!data) {
                throw new Base64Error('Data to decode cannot be empty');
            }

            let decodedData = data;

            // First, remove the key if present (before any transformations)
            if (key) {
                const keyEncoded = universalBtoa(key);
                decodedData = decodedData.replace(keyEncoded, '');
            }

            // Then apply URL-safe transformations if needed
            if (options.urlSafe) {
                decodedData = Base64Utils.makeUrlStandard(decodedData);
            }

            // Finally, handle padding
            if (options.padding && decodedData.length % 4 !== 0) {
                decodedData += '='.repeat(4 - (decodedData.length % 4));
            }

            return universalAtob(decodedData);
        } catch (error) {
            if (error instanceof Base64Error) {
                throw error;
            }
            throw new Base64Error(`Decoding failed: ${(error as Error).message}`);
        }
    }

    /**
     * Checks if a string is valid Base64
     * @param str - String to validate
     * @returns boolean
     */
    public static isBase64(str: string): boolean {
        try {
            if (!str) return false;
            // Tách chuỗi thành các phần nhỏ theo padding (== hoặc =)
            // Regex này sẽ giữ lại dấu = ở cuối mỗi phần
            const parts = str.match(/([A-Za-z0-9+/_-]+={0,2})/g);
            if (!parts || parts.length === 0) return false;
            for (const part of parts) {
                // Bỏ qua phần rỗng
                if (!part) continue;
                // Chuẩn hóa về base64 chuẩn
                const normalized = part
                    .replace(/[-_]/g, '+')
                    .replace(/[^A-Za-z0-9+/=]/g, '');
                // Nếu không hợp lệ thì trả về false
                try {
                    if (universalBtoa(universalAtob(normalized)) !== normalized) {
                        return false;
                    }
                } catch {
                    return false;
                }
            }
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Converts standard Base64 to URL-safe Base64
     * @param str - Standard Base64 string
     * @returns URL-safe Base64 string
     */
    private static makeUrlSafe(str: string): string {
        return str.replace(/[+/=]/g, char => Base64Utils.URL_SAFE_CHARS[char]);
    }

    /**
     * Converts URL-safe Base64 to standard Base64
     * @param str - URL-safe Base64 string
     * @returns Standard Base64 string
     */
    private static makeUrlStandard(str: string): string {
        return str.replace(/[-_]/g, char => Base64Utils.STANDARD_CHARS[char]);
    }

    /**
     * Encodes data using the default key
     * @param data - String to encode
     * @returns Encoded string
     * @throws Base64Error
     */
    public static encodeWithDefaultKey(data: string): string {
        return Base64Utils.encode(data, DEFAULT_KEY);
    }

    /**
     * Decodes data using the default key
     * @param data - String to decode
     * @returns Decoded string
     * @throws Base64Error
     */
    public static decodeWithDefaultKey(data: string): string {
        return Base64Utils.decode(data, DEFAULT_KEY);
    }

    /**
     * Laravel-compatible encoding with key (no URL-safe transformation)
     * Matches the exact behavior of the Laravel Base64::encodeWithKey method
     * @param data - String to encode
     * @param key - Encryption key (defaults to DEFAULT_KEY)
     * @returns Encoded string
     * @throws Base64Error
     */
    public static encodeWithKey(data: string, key: string = DEFAULT_KEY): string {
        return Base64Utils.encode(data, key, { padding: true, urlSafe: false });
    }

    /**
     * Laravel-compatible decoding with key (no URL-safe transformation)
     * Matches the exact behavior of the Laravel Base64::decodeWithKey method
     * @param data - String to decode
     * @param key - Decryption key (defaults to DEFAULT_KEY)
     * @returns Decoded string
     * @throws Base64Error
     */
    public static decodeWithKey(data: string, key: string = DEFAULT_KEY): string {
        return Base64Utils.decode(data, key, { padding: true, urlSafe: false });
    }
}